import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { LangSwitcher } from "@/components/LangSwitcher";

export default function Layout({ children }) {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const navLink = ({ isActive }) =>
    `link-gold text-sm tracking-wide ${isActive ? "text-[#D4AF37]" : ""}`;

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          scrolled ? "bg-[#F9F8F6]/95 backdrop-blur border-b border-[#D4AF37]/20" : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-12 py-5">
          <Link to="/" className="font-serif text-xl md:text-2xl tracking-tight text-[#0A1128]" data-testid="brand-logo">
            French Heritage <span className="italic text-[#D4AF37]">Experiences</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-10">
            <NavLink to="/experiences" className={navLink} data-testid="nav-experiences">{t("nav.experiences")}</NavLink>
            <NavLink to="/gallery" className={navLink} data-testid="nav-gallery">{t("nav.gallery")}</NavLink>
            <NavLink to="/journal" className={navLink} data-testid="nav-journal">{t("nav.journal")}</NavLink>
            <NavLink to="/about" className={navLink} data-testid="nav-about">{t("nav.about")}</NavLink>
            <NavLink to="/contact" className={navLink} data-testid="nav-contact">{t("nav.contact")}</NavLink>
          </nav>
          <div className="hidden lg:flex items-center gap-6">
            <LangSwitcher />
            {user ? (
              <>
                {(user.role === "admin" || user.role === "partnerships" || user.role === "marketing") && (
                  <Link to="/crm" className="link-gold text-sm" data-testid="nav-crm">{t("nav.crm")}</Link>
                )}
                <Link to="/portal" className="link-gold text-sm" data-testid="nav-portal">{t("nav.portal")}</Link>
                <button onClick={logout} className="text-sm link-gold" data-testid="nav-logout">{t("nav.logout")}</button>
              </>
            ) : (
              <Link to="/login" className="link-gold text-sm" data-testid="nav-login">{t("nav.login")}</Link>
            )}
            <Link
              to="/book"
              className="bg-[#0A1128] text-[#F9F8F6] px-5 py-2.5 text-xs tracking-[0.2em] uppercase hover:bg-[#D4AF37] hover:text-[#0A1128] transition-colors duration-500"
              data-testid="cta-book"
            >
              {t("nav.book")}
            </Link>
          </div>
          <button
            className="lg:hidden text-[#0A1128]"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
        {open && (
          <div className="lg:hidden bg-[#F9F8F6] border-t border-[#D4AF37]/20 px-6 py-6 flex flex-col gap-5">
            <NavLink to="/experiences" className={navLink} data-testid="m-nav-experiences">{t("nav.experiences")}</NavLink>
            <NavLink to="/gallery" className={navLink} data-testid="m-nav-gallery">{t("nav.gallery")}</NavLink>
            <NavLink to="/journal" className={navLink}>{t("nav.journal")}</NavLink>
            <NavLink to="/about" className={navLink}>{t("nav.about")}</NavLink>
            <NavLink to="/contact" className={navLink}>{t("nav.contact")}</NavLink>
            <Link to="/book" className="bg-[#0A1128] text-[#F9F8F6] px-5 py-3 text-xs tracking-[0.2em] uppercase w-fit" data-testid="m-cta-book">
              {t("nav.book")}
            </Link>
            <div className="flex items-center gap-6 pt-2">
              <LangSwitcher />
              {user ? (
                <>
                  <Link to="/portal" className="link-gold text-sm">{t("nav.portal")}</Link>
                  <button onClick={logout} className="text-sm link-gold">{t("nav.logout")}</button>
                </>
              ) : (
                <Link to="/login" className="link-gold text-sm">{t("nav.login")}</Link>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 pt-20">{children}</main>

      <footer className="bg-[#0A1128] text-[#F9F8F6] mt-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <div className="font-serif text-2xl">
              French Heritage <span className="italic text-[#D4AF37]">Experiences</span>
            </div>
            <p className="mt-4 text-[#F9F8F6]/70 max-w-md leading-relaxed">{t("footer.tag")}</p>
          </div>
          <div>
            <div className="overline text-[#D4AF37] mb-4">{t("footer.links")}</div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/experiences" className="hover:text-[#D4AF37]">{t("nav.experiences")}</Link></li>
              <li><Link to="/journal" className="hover:text-[#D4AF37]">{t("nav.journal")}</Link></li>
              <li><Link to="/about" className="hover:text-[#D4AF37]">{t("nav.about")}</Link></li>
              <li><Link to="/contact" className="hover:text-[#D4AF37]">{t("nav.contact")}</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline text-[#D4AF37] mb-4">Paris · New York</div>
            <p className="text-sm text-[#F9F8F6]/70">bonjour@fhe.travel</p>
            <p className="text-sm text-[#F9F8F6]/70 mt-1">+33 1 00 00 00 00</p>
          </div>
        </div>
        <div className="border-t border-[#F9F8F6]/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row justify-between text-xs text-[#F9F8F6]/50">
            <span>© {new Date().getFullYear()} French Heritage Experiences. {t("footer.rights")}</span>
            <span className="mt-2 md:mt-0">{t("footer.legal")} · {t("footer.privacy")}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
